/*
 * @Description:
 * @Author: hejiajun
 * @Date: 2026-02-09 16:31:18
 * @LastEditors: BILYHO 627008265@qq.com
 * @LastEditTime: 2026-02-11 22:51:03
 */
// APP_CONFIG 通过new webpack.DefinePlugin进行注入
import appConfigs, { DEFAULT_ROUTE } from "./config/appConfig.js";
import registerApps from "./config/registerApps.js";
import { createImportMapScript } from "./config/utils.js";

// ==================== 常量定义 ====================
const MODULE_NAMES = {
  SINGLE_SPA: "single-spa",
  VUE: "vue",
  VUE_ROUTER: "vue-router",
  TDESIGN_VUE_NEXT: "tdesign-vue-next",
  PINIA: "pinia",
};

// ==================== 全局变量初始化 ====================
// eslint-disable-next-line no-undef
const isLocal = APP_CONFIG?.isLocal ?? false;
// eslint-disable-next-line no-undef
const importmapConfig = __IMPORTMAP_CONFIG__;

window.isLocal = isLocal;

// ==================== 工具函数 ====================
/**
 * 处理 import-map-overrides 配置（仅本地环境）
 */
function handleImportMapOverrides() {
  if (!isLocal) return null;

  const overrideMap = window.importMapOverrides?.getOverrideMap();
  if (overrideMap?.imports) {
    Object.assign(importmapConfig.imports, overrideMap.imports);
  }
  return overrideMap;
}

/**
 * 打印调试信息
 */
function printDebugInfo(overrideMap) {
  console.group("应用配置调试");
  // eslint-disable-next-line no-undef
  console.log("当前环境:", APP_CONFIG);
  console.log("overrideMap:", overrideMap);
  console.log("引入文件:", importmapConfig);
  console.log("引入模块路径:", appConfigs);
  console.groupEnd();
}

/**
 * 验证 SystemJS 是否可用
 */
function validateSystemJS() {
  if (!window.System) {
    console.error("SystemJS 未加载成功");
    return false;
  }
  return true;
}

/**
 * 加载核心模块
 * @returns {Promise<Array>} 模块加载结果
 */
async function loadCoreModules() {
  // Phase 1: single-spa and vue are independent of each other
  const [singleSpa, Vue] = await Promise.all([
    window.System.import(MODULE_NAMES.SINGLE_SPA),
    window.System.import(MODULE_NAMES.VUE),
  ]);

  // Mount Vue globally before phase 2 (vue-router/tdesign/pinia may need it)
  window.Vue = Vue;

  // Phase 2: vue-router, tdesign, pinia can now load in parallel
  const [vueRouter, TDesign, Pinia] = await Promise.all([
    window.System.import(MODULE_NAMES.VUE_ROUTER),
    window.System.import(MODULE_NAMES.TDESIGN_VUE_NEXT),
    window.System.import(MODULE_NAMES.PINIA),
  ]);

  const pinia = Pinia.createPinia();
  window.PiniaInstance = pinia;

  return [singleSpa, Vue, TDesign, vueRouter, pinia];
}

/**
 * 将模块挂载到 window 对象
 */
function exposeModulesToWindow(singleSpa, Vue, TDesign, vueRouter, pinia) {
  window.singleSpa = singleSpa;
  window.Vue = Vue;
  window.TDesign = TDesign;
  window.vueRouter = vueRouter;
  window.pinia = pinia; // 暴露 Pinia 实例到全局
}

/**
 * 预加载子应用样式（消除 FOUC）
 */
function preloadSubappStyles() {
  const blogCssHref = importmapConfig.imports["blog-css"];
  if (blogCssHref && !document.getElementById("blog-styles")) {
    const link = document.createElement("link");
    link.id = "blog-styles";
    link.rel = "stylesheet";
    link.href = blogCssHref;
    document.head.appendChild(link);
  }
}

// ==================== 主初始化函数 ====================
async function init() {
  // 0. 处理根路径重定向：访问 / 时重定向到 /blog

  if (window.location.pathname === "/") {
    window.history.replaceState(null, "", DEFAULT_ROUTE);
  }

  // 1. 处理 import-map-overrides 配置
  const overrideMap = handleImportMapOverrides();

  // 2. 创建 systemjs-importmap
  createImportMapScript("systemjs-importmap", importmapConfig);

  // 3. 打印调试信息
  if (isLocal) printDebugInfo(overrideMap);

  // 4. 验证 SystemJS
  if (!validateSystemJS()) return;

  // 5. 加载核心模块
  try {
    const [singleSpa, Vue, TDesign, vueRouter, pinia] = await loadCoreModules();
    if (isLocal) {
      console.log("模块加载结果:", [singleSpa, Vue, TDesign, vueRouter, pinia]);
    }

    // 6. 验证模块加载结果
    if (!singleSpa) {
      console.error("singleSpa 未加载成功");
      return;
    }

    // 7. 暴露模块到全局
    exposeModulesToWindow(singleSpa, Vue, TDesign, vueRouter, pinia);

    // 8. 预加载子应用样式（消除 FOUC）
    preloadSubappStyles();

    // 9. 注册应用并启动（传递 Pinia 实例）
    registerApps({
      singleSpa,
      System: window.System,
      appConfigs,
      overrideMap,
      pinia, // 传递 Pinia 实例给子应用
    });
    singleSpa.start();

    // 10. 独立加载智能助理（直接挂载到 #assistant-app，不参与 single-spa 生命周期）
    window.System.import("assistant").catch((err) => {
      console.error("❌ 助理加载失败:", err);
    });
  } catch (error) {
    console.error("核心模块加载失败:", error);
    const el = document.createElement("div");
    el.style.cssText =
      "position:fixed;top:0;left:0;width:100%;padding:16px;background:#c0392b;color:#fff;font-size:16px;text-align:center;z-index:9999;";
    el.textContent = "应用加载失败，请刷新页面重试。";
    document.body.appendChild(el);
  }
}

// ==================== 启动初始化 ====================
window.addEventListener("DOMContentLoaded", init);
